import "./carousel.css";
import React, {useEffect, useMemo, useRef, useState} from "react";
import LongArrowLeft from "../../assets/icons/LongArrowLeft";
import LongArrowRight from "../../assets/icons/LongArrowRight";

interface CarouselItem {
    id: number;
    title: string;
    description: string;
    icon: string; // Use an emoji as a string like "😊"
};

interface CarouselProps {
    id: string;
    items: CarouselItem[];
    maxItemsPerView?: number;
    onChange: (id: number) => void;
    selectedId?: number | null;
}

const getId = (id: string, itemId: number) => {
    return `${id}-item-${itemId}`;
}

const CarouselItem = ({ id, item, onSelect, selectedId }: { id: string, item: CarouselItem, onSelect: () => void, selectedId: number | null }) => {
    const nodeId = getId(id, item.id);
    return (
        <div className={`carousel-item ${selectedId === item.id ? 'selected' : ''}`} key={nodeId} id={nodeId} onClick={onSelect}>
            {/*<div className="background-image" style={{ backgroundImage: `url(${WhatsappDefaultBackground})` }} />*/}
            <div className="emoji"><div className="emoji-inner">{item.icon}</div></div>
            <div className="border-bottom" />
            <div className="title">{item.title}</div>
            <div className="description">{item.description}</div>
        </div>
    );
}

const Carousel = ({ id, selectedId, items, maxItemsPerView = 5, onChange }: CarouselProps) => {
    const carouselItems = useRef<HTMLDivElement | null>(null);
    const container = useRef<HTMLDivElement | null>(null);
    const left = useRef(0);
    const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
    const [wasLoaded, setWasLoaded] = useState(false);

    const scrollWidth = useMemo(() => {
        const rect = carouselItems.current?.getBoundingClientRect()
        return rect?.width || 0;
    }, [items, maxItemsPerView]);

    const containerWidth = useMemo(() => {
        const rect = container.current?.getBoundingClientRect();
        return rect?.width || 0;
    }, []);

    const updateItemsSizes = () => {
        carouselItems.current && Array.from(carouselItems.current.children).forEach(item => {
            const rect = item.getBoundingClientRect();
            const itemWidth = rect.width;
            const screenWidth = window.innerWidth;
            const middleAreaMin = screenWidth / 2 - itemWidth / 2;
            const middleAreaMax = screenWidth / 2 + itemWidth / 2;
            const grace = rect.width * 0.1;
            if(rect.left >= middleAreaMin - grace && rect.right <= middleAreaMax + grace) {
                item.classList.add('selected');
            } else {
                item.classList.remove('selected');
            }
        });
    }

    const onMovingCarousel = () => {
        const leftBoundary = 0;
        const rightBoundary = -(scrollWidth / 3 * 2) + containerWidth;
        if(left.current >= leftBoundary) {
            carouselItems.current?.classList.remove('with-animation');
            left.current = left.current - scrollWidth / 3;
            carouselItems.current?.classList.add('with-animation');
        } else if(left.current <= rightBoundary) {
            carouselItems.current?.classList.remove('with-animation');
            left.current = left.current + scrollWidth / 3;
            carouselItems.current?.classList.add('with-animation');
        }
        updateItemsSizes();
    }

    const onWheel = (event: React.WheelEvent<HTMLElement>) => {
        const deltaX = event.deltaX;
        if(!carouselItems.current) return;
        left.current = left.current + deltaX;
        onMovingCarousel();
        carouselItems.current.style.left = `${left.current}px`;
    }

    const onSelect = (itemId: number) => () => {
        const selectedItem = document.getElementById(`2-${getId(id, itemId)}`);
        if(!carouselItems.current || !selectedItem) return;
        const rect = selectedItem.getBoundingClientRect();
        const itemPadding = 50;
        const itemLeft = rect.left;
        const itemWidth = rect.width;
        const step = itemWidth + itemPadding;
        const screenWidth = window.innerWidth;
        const centerLeft = screenWidth / 2 - itemWidth / 2;
        const delta = itemLeft - centerLeft;
        const steps = (Math.abs(scrollWidth / 3) - Math.abs(delta));
        if(delta > steps) {
            carouselItems.current?.classList.remove('with-animation');
            left.current = left.current - scrollWidth / 3;
            carouselItems.current.style.left = `${left.current}px`;
            setTimeout(() => {
                carouselItems.current?.classList.add('with-animation');
                left.current = left.current + steps;
                if(carouselItems.current) {
                    carouselItems.current.style.left = `${left.current}px`;
                }
            });
        } else if(delta < -steps) {
            carouselItems.current?.classList.remove('with-animation');
            left.current = left.current + scrollWidth / 3;
            carouselItems.current.style.left = `${left.current}px`;
            setTimeout(() => {
                carouselItems.current?.classList.add('with-animation');
                left.current = left.current - steps;
                if(carouselItems.current) {
                    carouselItems.current.style.left = `${left.current}px`;
                }
            });
        } else {
            carouselItems.current?.classList.add('with-animation');
            left.current = left.current - delta;
            carouselItems.current.style.left = `${left.current}px`;
        }
        setTimeout(() => {
            updateItemsSizes();
            setSelectedItemId(itemId);
            onChange(itemId);
        }, 250);
    }

    const moveStep = (direction: 'left' | 'right') => () => {
        if(selectedItemId === null) return;
        direction === 'left' && onSelect(selectedItemId > 1 ? selectedItemId - 1 : items.length)();
        direction === 'right' && onSelect(selectedItemId < items.length ? selectedItemId + 1 : 1)();
    }

    useEffect(() => {
        const defaultSelected = selectedId || 3;
        const selectedItem = document.getElementById(`2-${getId(id, defaultSelected)}`);
        if(!carouselItems.current || !selectedItem || !items?.length || wasLoaded) return;
        const rect = selectedItem.getBoundingClientRect();
        const itemPadding = 50;
        const itemLeft = rect.left;
        const itemWidth = rect.width;
        const screenWidth = window.innerWidth;
        const centerLeft = screenWidth / 2 - itemWidth / 2;
        const delta = itemLeft - centerLeft;
        left.current = left.current - delta;
        carouselItems.current?.classList.remove('with-animation');
        carouselItems.current.style.left = `${left.current}px`;
        setTimeout(() => {
            carouselItems.current?.classList.add('with-animation');
            updateItemsSizes();
        });
        setSelectedItemId(defaultSelected);
        onChange(defaultSelected);
        setWasLoaded(true);
    }, [items]);

    useEffect(() => {
        selectedId && selectedId !== selectedItemId && setSelectedItemId(selectedId);
    }, [selectedId]);

    return <div className="carousel-container" ref={container}>
        <div className="arrow left" onClick={moveStep('left')}><LongArrowLeft width={50} height={60} color='#fff' /></div>
        <div className="arrow right" onClick={moveStep('right')}><LongArrowRight width={50} height={60} color='#fff' /></div>
        <div className="carousel-items" ref={carouselItems} style={{ left: 0 }}>
            {items.map(item => <CarouselItem key={`1-${item.id}`} id={`1-${id}`} item={item} onSelect={onSelect(item.id)} selectedId={selectedItemId} />)}
            {items.map(item => <CarouselItem key={`2-${item.id}`} id={`2-${id}`} item={item} onSelect={onSelect(item.id)} selectedId={selectedItemId} />)}
            {items.map(item => <CarouselItem key={`3-${item.id}`} id={`3-${id}`} item={item} onSelect={onSelect(item.id)} selectedId={selectedItemId} />)}
        </div>
    </div>
}

export default Carousel;